import { Alert } from "@mui/material";
import MDBox from "components/MDBox";
import UiTypography from "components/base/UiTypography";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { HelperResolution } from "utils/worksheet/worksheetHelpers";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import {
  WorksheetInputField,
  WorksheetReadOnlyField,
  WorksheetSelectField,
} from "./WorksheetFormFields";

const SOURCE_OPTIONS = [
  { value: "item", label: "Job Size Measurement" },
  { value: "material", label: "Material quantity" },
];

const PERIOD_OPTIONS = [
  { value: "hour", label: "Per hour" },
  { value: "day", label: "Per workday" },
];

type WorksheetProductionTabProps = {
  values: WorksheetItemInput;
  productionUnit: string;
  materialOptions: { value: string; label: string }[];
  materialRowCount: number;
  hourlyRows: { index: number }[];
  resolution: HelperResolution;
  isReadOnly?: boolean;
  renderToggle: (index: number) => React.ReactNode;
};

export default function WorksheetProductionTab({
  values,
  productionUnit,
  materialOptions,
  materialRowCount,
  hourlyRows,
  resolution,
  isReadOnly,
  renderToggle,
}: WorksheetProductionTabProps) {
  const production = values.helpers?.production;
  const productionRate = production?.rate ?? "";

  return (
    <>
      <WorksheetSelectField
        name="helpers.production.source"
        label="Production quantity source"
        options={SOURCE_OPTIONS}
        fallback="item"
        isReadOnly={isReadOnly}
      />
      {production?.source === "material" &&
        (materialRowCount ? (
          <WorksheetSelectField
            name="helpers.production.materialRowUuid"
            label="Material"
            options={materialOptions}
            isReadOnly={isReadOnly}
          />
        ) : (
          <Alert severity="info">
            Add a material cost first, then use its quantity for job time.
          </Alert>
        ))}
      <MDBox
        display="grid"
        gridTemplateColumns="repeat(2, minmax(0, 1fr))"
        alignItems="start"
        gap="12px"
      >
        <WorksheetInputField
          name="helpers.production.rate"
          label="Production Rate"
          placeholder="0"
          isReadOnly={isReadOnly}
        />
        <WorksheetReadOnlyField label="Production Unit" value={productionUnit} />
        <WorksheetSelectField
          name="helpers.production.period"
          label="Rate Period"
          options={PERIOD_OPTIONS}
          fallback="hour"
          isReadOnly={isReadOnly}
        />
        <WorksheetInputField
          name="helpers.hoursPerDay"
          label="Working Hours per Day"
          placeholder="8"
          isReadOnly={isReadOnly}
        />
      </MDBox>
      {!productionRate.trim() && (
        <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
          Enter a production rate to enable job time.
        </UiTypography>
      )}
      {production?.enabled && resolution.hours !== null && (
        <Alert severity="info">
          Calculated Job Time: {resolution.hours.toDecimalPlaces(2).toString()} hours
          {resolution.days !== null ? ` · ${resolution.days} day(s)` : ""}
        </Alert>
      )}
      <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
        Working duration is elapsed time, not total employee-hours. Link hourly costs that follow
        it.
      </UiTypography>
      {hourlyRows.length > 0 ? (
        hourlyRows.map(({ index }) => renderToggle(index))
      ) : (
        <UiTypography uiVariant="body-sm" sx={{ color: draftColors.textSecondary }}>
          Add an hourly labor or equipment cost to link job time.
        </UiTypography>
      )}
    </>
  );
}
